import React from "react";
import { Avatar, Button, Flex, Loader, Text } from "@fluentui/react-northstar";
import HeaderBackButton from "../../components/headers/HeaderBackButton";
import { Container, ContainerFluid } from "../../common/styles";
import CustomBreadCrumb from "../../components/breadcrumb";
import styled from "styled-components";
import { CustomBadge } from "../../components/Badge";
import useLogicDetailDocument from "./hooks/useLogicDetailDocument";
import { formatFileSize } from "../../utils/functions";
import PdfViewerComponent from "../../components/PdfViewerComponent";
import DialogDeleteDocument from "./DialogDeleteDocument";
import moment from "moment";

const DetailDocument = () => {
    const { navigation, query, download, delete_document } =
        useLogicDetailDocument();

    return (
        <ContainerFluid column>
            <CustomBreadCrumb
                navList={[
                    {
                        label: "Documents RH",
                        href: "/documents",
                    },
                ]}
            />
            <HeaderBackButton
                title="Détails du document"
                onBack={navigation.goBack}
            />
            <Container column>
                {query.isLoading ? (
                    <Loader label="Chargement ..." />
                ) : (
                    <Flex>
                        <Left column>
                            <Flex space="between" vAlign="start">
                                <Flex column gap="gap.smaller">
                                    <Flex
                                        column
                                        styles={{
                                            overflow: "hidden",
                                            width: "300px",
                                        }}
                                    >
                                        <Title
                                            truncated
                                            content={query.data?.name}
                                        />
                                    </Flex>
                                    <Flex vAlign="start">
                                        {(query.data?.category_name ||
                                            query.data?.client_name) && (
                                            <CustomBadge
                                                bg={"#f0f0f0"}
                                                color="#242424"
                                                label={
                                                    query.data?.category_name ||
                                                    query.data?.client_name
                                                }
                                                rectangular
                                            />
                                        )}

                                        {query.data?.leave_details && (
                                            <CustomBadge
                                                bg={
                                                    query.data?.leave_details
                                                        ?.color
                                                }
                                                color="#242424"
                                                label={
                                                    query.data?.leave_details
                                                        ?.name
                                                }
                                                rectangular
                                            />
                                        )}
                                    </Flex>
                                </Flex>
                                <Flex column gap="gap.smaller" hAlign="end">
                                    <Flex column>
                                        <FileSize
                                            content={formatFileSize(
                                                query.data?.size
                                            )}
                                            truncated
                                        />
                                    </Flex>
                                    <Flex>
                                        <Date
                                            content={moment(
                                                query.data?.created_at
                                            ).format("DD/MM/YYYY")}
                                        />
                                    </Flex>
                                </Flex>
                            </Flex>
                            <Flex fill gap="gap.large" column>
                                {/* User card */}
                                {query.data?.user?.id && (
                                    <CardUser>
                                        <Flex column gap="gap.smaller">
                                            <Avatar
                                                name={query.data?.user?.name}
                                                image={query.data?.user?.avatar}
                                                size="large"
                                            />
                                        </Flex>
                                        <FlexTruncated column>
                                            <UserName
                                                content={query.data?.user?.name}
                                                truncated
                                            />
                                            <TitleJob
                                                content={
                                                    query.data?.user?.job_title
                                                }
                                                truncated
                                            />
                                        </FlexTruncated>
                                    </CardUser>
                                )}
                                <div>
                                    <AddedByLabel
                                        content={
                                            <div>
                                                <span>
                                                    {query.data?.creation_type}
                                                </span>
                                                <b>
                                                    &nbsp;
                                                    {
                                                        query.data?.created_by
                                                            ?.name
                                                    }
                                                </b>
                                            </div>
                                        }
                                    />
                                </div>
                                <Flex
                                    vAlign="center"
                                    hAlign="center"
                                    gap="gap.medium"
                                    column
                                >
                                    <ButtonDownload
                                        loading={download.isLoading}
                                        onClick={download.handleDownload}
                                        content="Télécharger"
                                        primary
                                        flat
                                    />
                                    {query?.data?.document_type === "hr" && (
                                        <DialogDeleteDocument
                                            isDeleting={
                                                delete_document.isLoading
                                            }
                                            handleConfirmDeleteDocument={
                                                delete_document.handleDelete
                                            }
                                        />
                                    )}
                                </Flex>
                            </Flex>
                        </Left>
                        <Right>
                            <PdfViewerComponent
                                documentId={query.data?.pspdfkit_document_id}
                                jwt={query.data?.pspdfkit_jwt}
                                enableAnnotation={false}
                            />
                        </Right>
                    </Flex>
                )}
            </Container>
        </ContainerFluid>
    );
};

export default DetailDocument;

const FlexTruncated = styled(Flex)`
    text-overflow: ellipsis;
    overflow: hidden;
`;

const ButtonDelete = styled(Button)`
    color: var(--color-danger);
    text-decoration: underline;
`;

const ButtonDownload = styled(Button)`
    width: 240px;
`;

const AddedByLabel = styled(Text)`
    color: var(
        --light-theme-rest-foreground-default-foreground-1424242,
        #424242
    );

    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px; /* 142.857% */
`;

const AddedByUser = styled(AddedByLabel)`
    font-weight: 600;
`;

const TitleJob = styled(Text)`
    color: var(--Light-theme-Rest-Foreground-Default-Foreground-2, #616161);

    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 16px; /* 133.333% */
`;

const UserName = styled(Text)`
    color: var(
        --light-theme-rest-foreground-default-foreground-242424,
        #242424
    );

    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px; /* 142.857% */
`;

const CardUser = styled(Flex)`
    display: flex;
    width: 240px;
    height: fit-content;
    padding: 16px 20px;
    align-items: center;
    gap: 16px;

    border-radius: 4px;
    border: 1px solid var(--Light-theme-Rest-Border-Default-Border, #d1d1d1);
    background: var(--Light-theme-Rest-Background-Default-Background, #fff);
`;

const Date = styled(Text)`
    color: var(
        --light-theme-rest-foreground-default-foreground-242424,
        #242424
    );
    text-align: right;

    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px; /* 142.857% */
`;

const FileSize = styled(Text)`
    color: var(
        --light-theme-rest-foreground-default-foreground-1424242,
        #424242
    );
    text-align: right;

    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px; /* 142.857% */
`;

const Title = styled(Text)`
    color: #000;

    font-size: 18px;
    font-style: normal;
    font-weight: 600;
    line-height: 24px; /* 133.333% */
`;

const Left = styled(Flex)`
    width: 429px;
    height: 460px;
    padding: 24px;
    gap: var(--spacing-xxxl-48, 48px);
`;

const Right = styled(Flex)`
    flex: 1;
    background-color: grey;
    height: calc(100vh - 200px);
`;
